/* PURE SKY - Main JavaScript */

// ========================================
// UTILITY FUNCTIONS
// ========================================

// Debounce function for performance
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle function for scroll events
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// ========================================
// MOBILE MENU
// ========================================

function initMobileMenu() {
  const toggle = document.querySelector('.mobile-menu-toggle');
  const nav = document.querySelector('.header__nav');
  const overlay = document.createElement('div');
  overlay.className = 'overlay';
  document.body.appendChild(overlay);

  if (!toggle || !nav) return;

  function openMenu() {
    nav.classList.add('active');
    overlay.classList.add('active');
    document.body.classList.add('modal-open');
  }

  function closeMenu() {
    nav.classList.remove('active');
    overlay.classList.remove('active');
    document.body.classList.remove('modal-open');
  }

  toggle.addEventListener('click', () => {
    if (nav.classList.contains('active')) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  overlay.addEventListener('click', closeMenu);

  // Mobile submenu handling
  const navItems = nav.querySelectorAll('.nav__item');
  navItems.forEach(item => {
    const link = item.querySelector('.nav__link');
    const megaMenu = item.querySelector('.mega-menu');
    
    if (megaMenu) {
      link.addEventListener('click', (e) => {
        if (window.innerWidth < 1024) {
          e.preventDefault();
          item.classList.toggle('active');
        }
      });
    }
  });
}

// ========================================
// HERO SLIDESHOW
// ========================================

function initHeroSlideshow() {
  const slideshow = document.querySelector('.hero-slideshow');
  if (!slideshow) return;

  const slides = slideshow.querySelector('.hero-slideshow__slides');
  const slideElements = slides.querySelectorAll('.hero-slide');
  const dots = slideshow.querySelectorAll('.hero-slideshow__dot');
  const prevBtn = slideshow.querySelector('.hero-slideshow__arrow--prev');
  const nextBtn = slideshow.querySelector('.hero-slideshow__arrow--next');

  let currentSlide = 0;
  const totalSlides = slideElements.length;
  let autoplayInterval;

  function goToSlide(index) {
    currentSlide = (index + totalSlides) % totalSlides;
    slides.style.transform = `translateX(-${currentSlide * 100}%)`;
    
    // Update dots
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === currentSlide);
    });
  }

  function nextSlide() {
    goToSlide(currentSlide + 1);
  }

  function prevSlide() {
    goToSlide(currentSlide - 1);
  }

  function startAutoplay() {
    autoplayInterval = setInterval(nextSlide, 6000);
  }

  function stopAutoplay() {
    clearInterval(autoplayInterval);
  }

  // Event listeners
  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => {
      goToSlide(index);
      stopAutoplay();
      startAutoplay();
    });
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      prevSlide();
      stopAutoplay();
      startAutoplay();
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      nextSlide();
      stopAutoplay();
      startAutoplay();
    });
  }

  // Pause on hover
  slideshow.addEventListener('mouseenter', stopAutoplay);
  slideshow.addEventListener('mouseleave', startAutoplay);

  // Touch/swipe support
  let touchStartX = 0;
  let touchEndX = 0;

  slideshow.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
  });

  slideshow.addEventListener('touchend', (e) => {
    touchEndX = e.changedTouches[0].screenX;
    handleSwipe();
  });

  function handleSwipe() {
    if (touchEndX < touchStartX - 50) {
      nextSlide();
      stopAutoplay();
      startAutoplay();
    }
    if (touchEndX > touchStartX + 50) {
      prevSlide();
      stopAutoplay();
      startAutoplay();
    }
  }

  // Start autoplay
  startAutoplay();
}

// ========================================
// BENEFITS SLIDER
// ========================================

function initBenefitsSlider() {
  const slider = document.querySelector('.benefits-slider');
  if (!slider) return;

  const container = slider.querySelector('.benefits-slider__container');
  const dots = slider.querySelectorAll('.benefits-slider__dot');
  const cards = container.querySelectorAll('.benefit-card');
  
  let currentPage = 0;
  const cardsPerPage = window.innerWidth < 768 ? 1 : 3;
  const totalPages = Math.ceil(cards.length / cardsPerPage);

  function goToPage(page) {
    currentPage = page;
    const translateX = -(currentPage * (100 / (cards.length / cardsPerPage)));
    container.style.transform = `translateX(${translateX}%)`;
    
    dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === currentPage);
    });
  }

  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => goToPage(index));
  });

  // Touch/swipe support
  let touchStartX = 0;
  let touchEndX = 0;

  slider.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
  });

  slider.addEventListener('touchend', (e) => {
    touchEndX = e.changedTouches[0].screenX;
    handleSwipe();
  });

  function handleSwipe() {
    if (touchEndX < touchStartX - 50 && currentPage < totalPages - 1) {
      goToPage(currentPage + 1);
    }
    if (touchEndX > touchStartX + 50 && currentPage > 0) {
      goToPage(currentPage - 1);
    }
  }

  // Reinit on resize
  window.addEventListener('resize', debounce(() => {
    goToPage(0);
  }, 250));
}

// ========================================
// QUANTITY SELECTOR
// ========================================

function initQuantitySelectors() {
  const selectors = document.querySelectorAll('.quantity-selector');

  selectors.forEach(selector => {
    const decreaseBtn = selector.querySelector('[data-action="decrease"]');
    const increaseBtn = selector.querySelector('[data-action="increase"]');
    const input = selector.querySelector('.quantity-selector__input');

    if (!decreaseBtn || !increaseBtn || !input) return;

    decreaseBtn.addEventListener('click', () => {
      const currentValue = parseInt(input.value) || 1;
      const min = parseInt(input.min) || 1;
      if (currentValue > min) {
        input.value = currentValue - 1;
      }
    });

    increaseBtn.addEventListener('click', () => {
      const currentValue = parseInt(input.value) || 1;
      const max = parseInt(input.max) || 999;
      if (currentValue < max) {
        input.value = currentValue + 1;
      }
    });

    input.addEventListener('change', () => {
      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 999;
      let value = parseInt(input.value) || min;
      
      if (value < min) value = min;
      if (value > max) value = max;
      
      input.value = value;
    });
  });
}

// ========================================
// VARIANT SELECTOR
// ========================================

function initVariantSelectors() {
  const selectors = document.querySelectorAll('.variant-selector');

  selectors.forEach(selector => {
    const options = selector.querySelectorAll('.variant-option');

    options.forEach(option => {
      option.addEventListener('click', () => {
        // Remove active class from siblings
        options.forEach(opt => opt.classList.remove('active'));
        // Add active class to clicked option
        option.classList.add('active');
      });
    });
  });
}

// ========================================
// QUICK-SHOP GALLERY
// ========================================

function initQuickShopGallery() {
  const quickShop = document.querySelector('.quick-shop');
  if (!quickShop) return;

  const mainImage = quickShop.querySelector('#main-product-image');
  const thumbnails = quickShop.querySelectorAll('.quick-shop__thumbnail');

  thumbnails.forEach(thumbnail => {
    thumbnail.addEventListener('click', () => {
      // Remove active class from all thumbnails
      thumbnails.forEach(thumb => thumb.classList.remove('active'));
      // Add active to clicked thumbnail
      thumbnail.classList.add('active');
      
      // Update main image
      const fullImageSrc = thumbnail.querySelector('img').getAttribute('data-full');
      if (fullImageSrc && mainImage) {
        mainImage.src = fullImageSrc;
      }
    });
  });
}

// ========================================
// ADD TO CART
// ========================================

function initAddToCart() {
  const addToCartButtons = document.querySelectorAll('.btn-primary:not(.quick-add)');

  addToCartButtons.forEach(button => {
    if (button.textContent.includes('Add to Cart')) {
      button.addEventListener('click', handleAddToCart);
    }
  });

  function handleAddToCart(e) {
    const button = e.currentTarget;
    const originalText = button.textContent;

    // Add loading state
    button.classList.add('btn-loading');
    button.disabled = true;

    // Simulate API call
    setTimeout(() => {
      // Remove loading, show success
      button.classList.remove('btn-loading');
      button.textContent = 'Added!';
      button.style.backgroundColor = 'var(--color-success)';

      // Update cart count
      updateCartCount();

      // Reset button after 2 seconds
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
        button.disabled = false;
      }, 2000);
    }, 800);
  }
}

function updateCartCount() {
  const cartBadge = document.querySelector('.cart-count__badge');
  if (cartBadge) {
    const currentCount = parseInt(cartBadge.textContent) || 0;
    cartBadge.textContent = currentCount + 1;
  }
}

// ========================================
// NEWSLETTER FORM
// ========================================

function initNewsletterForm() {
  const form = document.querySelector('.newsletter-form');
  if (!form) return;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    
    const input = form.querySelector('.newsletter-form__input');
    const button = form.querySelector('.btn');
    const originalText = button.textContent;

    // Validate email
    if (!input.value || !input.validity.valid) {
      input.focus();
      return;
    }

    // Loading state
    button.classList.add('btn-loading');
    button.disabled = true;

    // Simulate API call
    setTimeout(() => {
      button.classList.remove('btn-loading');
      button.textContent = 'Subscribed!';
      button.style.backgroundColor = 'var(--color-success)';
      input.value = '';

      // Reset after 3 seconds
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
        button.disabled = false;
      }, 3000);
    }, 1000);
  });
}

// ========================================
// SCROLL TO TOP
// ========================================

function initScrollToTop() {
  const scrollBtn = document.createElement('button');
  scrollBtn.className = 'scroll-to-top';
  scrollBtn.innerHTML = '↑';
  scrollBtn.setAttribute('aria-label', 'Scroll to top');
  scrollBtn.style.cssText = `
    position: fixed;
    bottom: 24px;
    right: 24px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--color-accent);
    color: var(--color-white);
    font-size: 24px;
    border: none;
    cursor: pointer;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    z-index: 1000;
    box-shadow: var(--shadow-lg);
  `;

  document.body.appendChild(scrollBtn);

  // Show/hide on scroll
  window.addEventListener('scroll', throttle(() => {
    if (window.scrollY > 500) {
      scrollBtn.style.opacity = '1';
      scrollBtn.style.visibility = 'visible';
    } else {
      scrollBtn.style.opacity = '0';
      scrollBtn.style.visibility = 'hidden';
    }
  }, 200));

  // Scroll to top on click
  scrollBtn.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

// ========================================
// LAZY LOADING IMAGES
// ========================================

function initLazyLoading() {
  const images = document.querySelectorAll('img[loading="lazy"]');
  
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src || img.src;
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }
}

// ========================================
// FADE-IN ANIMATION ON SCROLL
// ========================================

function initScrollAnimations() {
  const elements = document.querySelectorAll('.section, .product-card, .happiness-card, .benefit-card');
  
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });

    elements.forEach(el => observer.observe(el));
  }
}

// ========================================
// INITIALIZE ALL
// ========================================

document.addEventListener('DOMContentLoaded', () => {
  initMobileMenu();
  initHeroSlideshow();
  initBenefitsSlider();
  initQuantitySelectors();
  initVariantSelectors();
  initQuickShopGallery();
  initAddToCart();
  initNewsletterForm();
  initScrollToTop();
  initLazyLoading();
  initScrollAnimations();

  // Log initialization
  console.log('PURE SKY - Website initialized ✓');
});
